import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AlarmsByCategoriesList from './AlarmsByCategoriesList'
import AlarmList from './AlarmList'
import { ActionCreators, AlarmUtilsWithExtras, Selectors } from 'galarm-shared'
import { Box, Layer } from 'grommet'
import { Add } from 'grommet-icons'
import { I18n, Constants } from 'galarm-config'
import { colorThemes } from 'galarm-config'
import { Fab, Action } from 'react-tiny-fab'
import TintedImage from './TintedImage'
import EditAlarmCategory from './EditAlarmCategory'
import RecentlyDeletedAlarmList from './RecentlyDeletedAlarmList'
import 'react-tiny-fab/dist/styles.css'
import { NavigationUtils } from 'galarm-ps-api'
import CalendarEventList from './CalendarEventList'
import AlertIncidents from './AlertIncidents'
import InstantAlarmList from './InstantAlarmList'
import ExpiredAlarmScreen from './ExpiredAlarmScreen'

const {
  makeAlarmsInCategorySelector,
  allAlarmsSelector,
  canCreateNewAlarmDataSelector,
  upgradePurchasedSelector,
  sortedCalendarEventsSelector,
  taskAlarmsSelector,
  numGroupAlarmsSelector,
  numBuddyAlarmsSelector
} = Selectors

// eslint-disable-next-line no-unused-vars
const NewAlarmFAB = () => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const canCreateNewAlarmData = useSelector(canCreateNewAlarmDataSelector)
  const upgradePurchased = useSelector(upgradePurchasedSelector)
  const mobileNumber = useSelector(state => state.userInfo.mobileNumber)
  const numGroupAlarms = useSelector(numGroupAlarmsSelector)
  const numBuddyAlarms = useSelector(numBuddyAlarmsSelector)
  const fabButtonBehavior = useSelector(
    state => state.userSettings.fabButtonBehavior
  )

  const [createNewCategory, setCreateNewCategory] = useState(false)

  const dispatch = useDispatch()
  const categorizedAlarmView = useSelector(
    state => state.userSettings.categorizedAlarmView
  )

  const currentlySelectedAlarmCategoryId = useSelector(
    state => state.appState.currentlySelectedAlarmCategoryId
  )

  const createAlarmCategory = alarmCategory =>
    dispatch(ActionCreators.createAlarmCategory(alarmCategory))

  const onNewAlarm = () => {
    dispatch(
      ActionCreators.showSelectAlarmTypeScreen({
        alarmCategoryId: currentlySelectedAlarmCategoryId
      })
    )
  }

  const onQuickReminder = () => {
    const canCreateNewAlarm =
      AlarmUtilsWithExtras.canCreateNewAlarmAndShowAlertsIfNeeded(
        canCreateNewAlarmData
      )
    if (!canCreateNewAlarm) {
      return
    }

    dispatch(ActionCreators.setShowQuickReminder(true))
  }

  const onPersonalAlarm = () => {
    const canCreateNewAlarm =
      AlarmUtilsWithExtras.canCreateNewAlarmAndShowAlertsIfNeeded(
        canCreateNewAlarmData
      )
    if (!canCreateNewAlarm) {
      return
    }

    dispatch(
      ActionCreators.showNewAlarmWizard({
        alarmType: Constants.AlarmTypes.CASCADING,
        alarmCategoryId:
          currentlySelectedAlarmCategoryId ||
          Constants.UNCATEGORIZED_ALARM_CATEGORY_ID,
        selectedGroup: null,
        selectedContacts: []
      })
    )
  }

  const onGroupAlarm = () => {
    if (!mobileNumber) {
      NavigationUtils.showAlert(
        I18n.t('registrationRequired'),
        I18n.t('phoneNumberRequiredGroupAlarmHeaderText')
      )
      return
    }

    if (!upgradePurchased && numGroupAlarms >= 1) {
      NavigationUtils.showGalarmPremiumScreen('unlimitedAlarmsPremiumFeature')
      return
    }

    dispatch(
      ActionCreators.showNewAlarmWizard({
        alarmType: Constants.AlarmTypes.SIMULTANEOUS,
        alarmCategoryId:
          currentlySelectedAlarmCategoryId ||
          Constants.UNCATEGORIZED_ALARM_CATEGORY_ID,
        selectedGroup: null,
        selectedContacts: []
      })
    )
  }

  const onBuddyAlarm = () => {
    if (!mobileNumber) {
      NavigationUtils.showAlert(
        I18n.t('registrationRequired'),
        I18n.t('phoneNumberRequiredBuddyAlarmHeaderText')
      )
      return
    }

    if (!upgradePurchased && numBuddyAlarms >= 1) {
      NavigationUtils.showGalarmPremiumScreen('unlimitedAlarmsPremiumFeature')
      return
    }

    dispatch(
      ActionCreators.showNewAlarmWizard({
        alarmType: Constants.AlarmTypes.RECIPIENT,
        alarmCategoryId:
          currentlySelectedAlarmCategoryId ||
          Constants.UNCATEGORIZED_ALARM_CATEGORY_ID,
        selectedContacts: []
      })
    )
  }

  const onInstantAlert = () => {
    dispatch(ActionCreators.showNewInstantAlarmWizard({}))
  }

  const onNewAlarmCategory = () => {
    if (!upgradePurchased) {
      NavigationUtils.showGalarmPremiumScreen('alarmCategoriesPremiumFeature')
      return
    }

    setCreateNewCategory(true)
  }

  const fabButtonStyle = {
    backgroundColor: colorThemes.getColorTheme(colorScheme).primary
  }

  const renderCreateNewCategoryLayer = () =>
    createNewCategory && (
      <Layer background="lightTint">
        <Box margin="medium">
          <EditAlarmCategory
            onSaveAlarmCategory={createAlarmCategory}
            onClose={() => setCreateNewCategory(false)}
          />
        </Box>
      </Layer>
    )

  if (fabButtonBehavior?.mode === Constants.FAB_BUTTON_BEHAVIOR_MODES.CUSTOM) {
    const customOptions = fabButtonBehavior.customOptions || {}
    const allCustomActions = [
      {
        key: Constants.FAB_BUTTON_OPTIONS.QUICK_REMINDER,
        text: I18n.t('quickAlarm'),
        onClick: onQuickReminder,
        src: require('galarm-res/img/web/ic_quick_alarm.svg').default
      },
      {
        key: Constants.FAB_BUTTON_OPTIONS.PERSONAL_ALARM,
        text: I18n.t('personalAlarm'),
        onClick: onPersonalAlarm,
        src: require('galarm-res/img/web/ic_personal_alarm.svg').default
      },
      {
        key: Constants.FAB_BUTTON_OPTIONS.GROUP_ALARM,
        text: I18n.t('groupAlarm'),
        onClick: onGroupAlarm,
        src: require('galarm-res/img/web/ic_group_alarm.svg').default
      },
      {
        key: Constants.FAB_BUTTON_OPTIONS.BUDDY_ALARM,
        text: I18n.t('buddyAlarm'),
        onClick: onBuddyAlarm,
        src: require('galarm-res/img/web/ic_buddy_alarm.svg').default
      },
      {
        key: Constants.FAB_BUTTON_OPTIONS.INSTANT_ALERT,
        text: I18n.t('instantAlarm'),
        onClick: onInstantAlert,
        src: require('galarm-res/img/web/ic_instant_alarm.svg').default
      }
    ]
    let enabledActions = allCustomActions.filter(a => customOptions[a.key])

    if (categorizedAlarmView && !currentlySelectedAlarmCategoryId) {
      enabledActions = [
        ...enabledActions,
        {
          key: 'newCategory',
          text: I18n.t('newAlarmCategoryTitle'),
          onClick: onNewAlarmCategory,
          src: require('galarm-res/img/web/ic_category_view.svg').default
        }
      ]
    }

    if (enabledActions.length <= 1) {
      const singleAction = enabledActions[0]
      return (
        <>
          <Fab
            mainButtonStyles={fabButtonStyle}
            style={{
              bottom: 0,
              left: 0
            }}
            icon={<Add color="white" />}
            event="hover"
            text={singleAction ? singleAction.text : undefined}
            onClick={singleAction ? singleAction.onClick : onNewAlarm}
          />
          {renderCreateNewCategoryLayer()}
        </>
      )
    }

    return (
      <>
        <Fab
          mainButtonStyles={fabButtonStyle}
          style={{
            bottom: 0,
            left: 0
          }}
          icon={<Add color="white" />}
          event="hover"
          alwaysShowTitle={true}>
          {enabledActions.map(action => (
            <Action
              key={action.key}
              text={action.text}
              onClick={action.onClick}
              style={fabButtonStyle}>
              <TintedImage tintColor="white" src={action.src} height="24px" />
            </Action>
          ))}
        </Fab>
        {renderCreateNewCategoryLayer()}
      </>
    )
  }

  return (
    <>
      <Fab
        mainButtonStyles={fabButtonStyle}
        style={{
          bottom: 0,
          left: 0
        }}
        icon={<Add color="white" />}
        event="hover"
        alwaysShowTitle={true}>
        <Action
          text={I18n.t('quickAlarm')}
          onClick={() => onQuickReminder()}
          style={fabButtonStyle}>
          <TintedImage
            tintColor="white"
            src={require('galarm-res/img/web/ic_quick_alarm.svg').default}
          />
        </Action>
        <Action
          text={I18n.t('newAlarm')}
          onClick={() => onNewAlarm()}
          style={fabButtonStyle}>
          <TintedImage
            tintColor="white"
            src={require('galarm-res/img/web/ic_alarm.svg').default}
          />
        </Action>
        {categorizedAlarmView && !currentlySelectedAlarmCategoryId && (
          <Action
            text={I18n.t('newAlarmCategoryTitle')}
            onClick={() => onNewAlarmCategory()}
            style={fabButtonStyle}>
            <TintedImage
              tintColor="white"
              src={require('galarm-res/img/web/ic_category_view.svg').default}
            />
          </Action>
        )}
      </Fab>
      {renderCreateNewCategoryLayer()}
    </>
  )
}

const UnifiedAlarmList = ({ showSearchBar, hideSearchBar }) => {
  const categorizedAlarmView = useSelector(
    state => state.userSettings.categorizedAlarmView
  )

  const showAlertIncidents = useSelector(
    state => state.appState.showAlertIncidents
  )

  const showExpiredAlarms = useSelector(
    state => state.appState.showExpiredAlarms
  )

  const showInstantAlarms = useSelector(
    state => state.appState.showInstantAlarms
  )

  const showTasks = useSelector(state => state.appState.showTasks)

  const showCalendarEvents = useSelector(
    state => state.appState.showCalendarEvents
  )

  const currentlySelectedAlarmCategoryId = useSelector(
    state => state.appState.currentlySelectedAlarmCategoryId
  )

  const showRecentlyDeletedAlarms = useSelector(
    state => state.appState.showRecentlyDeletedAlarms
  )

  const allAlarms = useSelector(state => allAlarmsSelector(state))
  const calendarEvents = useSelector(sortedCalendarEventsSelector)
  const taskAlarms = useSelector(taskAlarmsSelector)

  const alarmsInCategorySelector = makeAlarmsInCategorySelector()
  const alarmsInCategory = useSelector(state =>
    alarmsInCategorySelector(state, {
      id: currentlySelectedAlarmCategoryId
    })
  )
  const recentlyDeletedAlarms = useSelector(
    state => state.alarms.recentlyDeletedAlarms
  )

  if (showExpiredAlarms) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <ExpiredAlarmScreen
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (showAlertIncidents) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <AlertIncidents
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (showInstantAlarms) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <InstantAlarmList
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (showTasks) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <AlarmList
          alarms={taskAlarms}
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (showCalendarEvents) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <CalendarEventList
          events={calendarEvents}
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (showRecentlyDeletedAlarms) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <RecentlyDeletedAlarmList
          alarms={recentlyDeletedAlarms}
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
      </Box>
    )
  } else if (categorizedAlarmView) {
    if (currentlySelectedAlarmCategoryId) {
      return (
        <Box flex overflow="auto" style={{ position: 'relative' }}>
          <AlarmList
            alarms={alarmsInCategory}
            showSearchBar={showSearchBar}
            hideSearchBar={hideSearchBar}
          />
          <NewAlarmFAB />
        </Box>
      )
    }
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <AlarmsByCategoriesList />
        <NewAlarmFAB />
      </Box>
    )
  } else {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <AlarmList
          alarms={allAlarms}
          showSearchBar={showSearchBar}
          hideSearchBar={hideSearchBar}
        />
        <NewAlarmFAB />
      </Box>
    )
  }
}

export default UnifiedAlarmList
